import Constant from '@/common/constant/Constant';
import TelephoneConstant from '@/common/constant/TelephoneConstant';
import Utils from '@/common/utils/utils';
import { getTicketById } from '@/service/ticketService';
import { defineTargetProjectAndPanelWhenOpenTelephoneTicket } from '@/component/notification/handler/NotificationHandler';
import { getCurrentTelephoneStatusData } from '@/component/ticketList/handler/TicketListHandler';

/**
 * Open telephone ticket detail + chat panel
 * @param {Object} ticketData - { ticketId, chatThreadId }
 * @param {Object} projectInuseDetail - current project detail
 * @param {Array} projectList - list of projects user belongs to
 * @param {Function} addNewPanel - Recoil setter for panel
 * @param {Object|null} reloadPanelData - { ticketDetailPanelId, chatThreadPanelId } for reload existing panels
 * @param {Function} reloadTicketListByPanelType - Recoil setter for ticket list reload
 */
export const openTelephoneTicketPanel = async ({
    ticketData,
    projectInuseDetail,
    projectList,
    addNewPanel,
    reloadPanelData = null,
    reloadTicketListByPanelType = () => {},
}) => {
    try {
        const { ticketId, chatThreadId } = ticketData;
        if (!ticketId) return;

        const ticketDetail = await getTicketById(null, ticketId);
        if (!ticketDetail || Utils.isEmptyObject(ticketDetail)) return;

        await delayWaitTelephoneStatusChanged(ticketDetail);

        const metaData = Utils.convertJSONDataToObject(ticketDetail.metaData);
        const sourceInfo = {
            ticketCallType: metaData?.callType,
            affiliationProject: ticketDetail.affiliationProject,
            customer: ticketDetail.customer,
            projectTarget: Utils.convertJSONDataToObject(ticketDetail.projectTarget) || [],
            ticketType: ticketDetail.ticketType,
            ticketOutline: ticketDetail?.outline,
            ticketTitle: ticketDetail?.outline,
        };

        const argsAll = {
            projectInuseDetail,
            projectList,
        };

        const { panelType, projectTargetId } = await defineTargetProjectAndPanelWhenOpenTelephoneTicket(argsAll, sourceInfo);
        if (!panelType || !projectTargetId) return;

        const ticketDetailPanelType = panelType === Constant.PANEL_TYPE.HELPDESK_TICKET_LIST
            ? Constant.PANEL_TYPE.HELPDESK_TICKET_DETAIL
            : Constant.PANEL_TYPE.CUSTOMER_TICKET_DETAIL;

        const chatThreadPanelType = panelType === Constant.PANEL_TYPE.HELPDESK_TICKET_LIST
            ? Constant.PANEL_TYPE.HELPDESK_CHATTHREAD
            : Constant.PANEL_TYPE.CUSTOMER_CHATTHREAD;

        const newPanelId = reloadPanelData?.ticketDetailPanelId || Date.now().toString();
        await addNewPanel({
            type: ticketDetailPanelType,
            tabId: ticketId,
            ticketId: ticketId,
            newPanelId: newPanelId,
            ticketOutline: ticketDetail?.outline,
        });

        const ticketListType = panelType === Constant.PANEL_TYPE.HELPDESK_TICKET_LIST
            ? Constant.TYPE_GET_TICKET_LIST.HELPDESK
            : Constant.TYPE_GET_TICKET_LIST.CUSTOMER;
        handleTriggerReloadPanelList(ticketListType, ticketDetail, reloadTicketListByPanelType)

        const resolvedChatThreadId = chatThreadId || ticketDetail?.chatThreadWithCustomer;

        const chatPanelCurrentId = reloadPanelData?.ticketDetailPanelId || newPanelId;

        const openChatPanel = (threadId, timer = 500) => {
            return new Promise((resolve) => {
                setTimeout(async () => {
                    await addNewPanel({
                        type: chatThreadPanelType,
                        tabId: ticketId,
                        ticketId: ticketId,
                        currentPanelId: chatPanelCurrentId,
                        selectThreadId: threadId,
                        openRelatePanelType: ticketDetailPanelType,
                        ticketOutline: ticketDetail?.outline,
                        isOpenChat: true
                    });
                    resolve();
                }, timer);
            });
        };

        if (resolvedChatThreadId) {
            await openChatPanel(resolvedChatThreadId);
        } else {
            // Retry with polling: chatThread may not be created yet (race condition with createNewTicket Lambda)
            const MAX_RETRIES = 3;
            const RETRY_INTERVAL = 1500;
            let retryCount = 0;
            let retryChatThreadId = '';

            while (retryCount < MAX_RETRIES) {
                retryCount++;
                await new Promise(resolve => setTimeout(resolve, RETRY_INTERVAL));

                const retryTicket = await getTicketById(null, ticketId);
                retryChatThreadId = retryTicket?.chatThreadWithCustomer || '';
                if (retryChatThreadId) {
                    await openChatPanel(retryChatThreadId, 0);
                    break;
                }
            }
        }
    } catch (error) {
        console.error('Error in openTelephoneTicketPanel >>>', error);
    };
};

/**
 * Handle open telephone panel for retry or follow-up after callback
 * Retry (MISSING_CALL/CANCELLED_CALL): open existing ticket panel with reload
 * Follow-up (END_CALL): do nothing here — subscription handler opens new ticket panel
 *
 * @param {Object} params
 * @param {Object} params.currentTicketDetail - latest ticket data from checkDetailTicket
 * @param {Array} params.telephoneStatusItemList - static form field items (from ticket state)
 * @param {string|Object} params.telephoneStatusDetail - telephoneStatusDetail from ticket state (string JSON or object)
 * @param {string} params.panelType - current panel type (HELPDESK_TICKET_LIST or CUSTOMER_TICKET_LIST, etc.)
 * @param {Array} params.panelList - current panel list
 * @param {Object} params.projectInuseDetail - current project detail
 * @param {Array} params.projectList - list of projects
 * @param {Function} params.addNewPanel - Recoil setter for panel
 */
export const handleOpenPanelTelephoneRetryOrFollowUp = async (params) => {
    const {
        currentTicketDetail,
        telephoneStatusItemList,
        telephoneStatusDetail,
        panelType,
        panelList,
        projectInuseDetail,
        projectList,
        addNewPanel,
        reloadTicketListByPanelType
    } = params;

    if (!currentTicketDetail) return;

    // Resolve current telephone status sysField
    let currentStatusField = '';

    // Strategy 1: Use getCurrentTelephoneStatusData with telephoneStatusItemList
    if (!Utils.isEmptyArray(telephoneStatusItemList)) {
        const ticketForStatus = {
            ...currentTicketDetail,
            telephoneStatusItemList,
            telephoneStatusDetail
        };
        const statusData = getCurrentTelephoneStatusData(ticketForStatus);
        currentStatusField = statusData?.currentStatusField || '';
    }

    // Strategy 2: Parse telephoneStatusDetail string/object directly (DetailTicket.js case)
    if (!currentStatusField && telephoneStatusDetail) {
        const parsed = typeof telephoneStatusDetail === 'string'
            ? Utils.convertJSONDataToObject(telephoneStatusDetail)
            : telephoneStatusDetail;
        if (parsed?.sysField) {
            currentStatusField = parsed.sysField;
        }
    }

    const isRetry = [
        TelephoneConstant.TELEPHONE_CALL_STATUS.MISSING_CALL,
        TelephoneConstant.TELEPHONE_CALL_STATUS.CANCELLED_CALL
    ].includes(currentStatusField);

    // Follow-up case (END_CALL): subscription handler will open panel when new ticket is created
    if (!isRetry) return;

    // Retry case: open panel with current ticketId
    const isHelpdesk = panelType === Constant.PANEL_TYPE.HELPDESK_TICKET_LIST
        || panelType === Constant.PANEL_TYPE.HELPDESK_TICKET_DETAIL;

    const ticketDetailPanelType = isHelpdesk
        ? Constant.PANEL_TYPE.HELPDESK_TICKET_DETAIL
        : Constant.PANEL_TYPE.CUSTOMER_TICKET_DETAIL;
    const chatThreadPanelType = isHelpdesk
        ? Constant.PANEL_TYPE.HELPDESK_CHATTHREAD
        : Constant.PANEL_TYPE.CUSTOMER_CHATTHREAD;

    const ticketDetailPanel = panelList.find(p =>
        p.type === ticketDetailPanelType &&
        p.tabList?.some(tab => tab.id === currentTicketDetail.id)
    );
    const chatThreadPanel = panelList.find(p =>
        p.type === chatThreadPanelType &&
        p.tabList?.some(tab => tab.id === currentTicketDetail.id)
    );
    const reloadPanelData = (ticketDetailPanel || chatThreadPanel)
        ? { ticketDetailPanelId: ticketDetailPanel?.id, chatThreadPanelId: chatThreadPanel?.id }
        : null;

    // delay await update ticketStatus on sever, before open panel detail
    await Utils.delay(3000);

    openTelephoneTicketPanel({
        ticketData: { ticketId: currentTicketDetail.id, chatThreadId: currentTicketDetail.chatThreadWithCustomer },
        projectInuseDetail,
        projectList,
        addNewPanel,
        reloadPanelData,
        reloadTicketListByPanelType
    });
};

export const handleTriggerReloadPanelList = (ticketListType, ticketDetail, reloadTicketListByPanelType = () => {}) => {
    const triggerPanelType = ticketListType === Constant.TYPE_GET_TICKET_LIST.HELPDESK
        ? Constant.PANEL_TYPE.HELPDESK_TICKET_LIST
        : Constant.PANEL_TYPE.CUSTOMER_TICKET_LIST
    
    const telephoneMetaData = Utils.convertJSONDataToObject(ticketDetail?.metaData);
    
    const isTelephoneTraining = telephoneMetaData?.telephoneType === TelephoneConstant.METADATA_TELEPHONE_TYPE.TRAINING
    
    let viewType = isTelephoneTraining 
        ? Constant.TICKET_PROPERTY.TRAINING 
        : Constant.TICKET_PROPERTY.SENT // on helpdeskList
    
    if (triggerPanelType === Constant.PANEL_TYPE.CUSTOMER_TICKET_LIST) {
        viewType = Constant.CUSTOMER_SIDE_VIEW.TELEPHONE_VIEW // on qaList
    }

    reloadTicketListByPanelType({
        panelType: triggerPanelType,
        viewType: viewType
    });
};

export const delayWaitTelephoneStatusChanged = async (ticketDetail) => {
    if (!ticketDetail || !ticketDetail?.id) return false;
    let telephoneDetail = {...ticketDetail};

    console.time('[TIME] delayWaitTelephoneStatusChanged');
    const MAX_RETRY = 3;
    const DELAY_MS = 3000;
    let isOpenTelephonePanelDetail = false;

    for (let retry = 0; retry < MAX_RETRY; retry++) {
        // retry query telephoneDetail form retry = 1
        if (retry > 0) {
            telephoneDetail = await getTicketById(null, telephoneDetail.id);
        }

        const telephoneStatus = Utils.convertJSONDataToObject(telephoneDetail?.telephoneStatus);
        
        if (telephoneStatus?.statusSysField !== TelephoneConstant.TELEPHONE_CALL_STATUS.CALL_WAITING) {
            isOpenTelephonePanelDetail = true;
            console.log('retry count >>>', retry);
            console.timeEnd('[TIME] delayWaitTelephoneStatusChanged');

            return telephoneDetail;
        }

        await Utils.delay(DELAY_MS);
    }

    return telephoneDetail; // auto open
};
